import React, { useEffect, useState } from 'react';
import axiosWithAuth from '../../api/axiosWithAuth';
import chunk from 'lodash/chunk';
import LoadingSpiner from '../Loader/LoadingSpiner';
import ActionButton from '../Buttons/ActionButton';
import { FaFileCsv, FaFilePdf, FaPrint, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa6';
import { useFetchProjectQuery } from '../../api/project';
import { Link } from 'react-router-dom';
import { clearLocalStorage, fetchFromLocalStorage, saveToLocalStorage } from '../../api/localStorage';
import { FaArrowCircleRight } from 'react-icons/fa';
import DownloadCSVButton from './DownloadCSVButton';
import { ProcessedImageData, processImageData, sortProcessedData } from './reportUtils';
import { Result, Image } from './types';
import { useImageList } from '../Image/hooks/useImageList';

interface ReportPreviewProps {
    projectId: string;
}

type SortField = keyof ProcessedImageData;
type SortDirection = 'asc' | 'desc' | null;

export default function ReportPreview(props: ReportPreviewProps) {
    const { projectId } = props;
    const { 
        images, 
        isLoading, 
        isError 
    } = useImageList({ 
        projectId, 
        autoFetchAll: true // Automatically fetch all images for report generation
    });
    const { data: project, isLoading: isProjectLoading, isError: isProjectError } = useFetchProjectQuery(projectId);

    const [loading, setLoading] = useState<boolean>(true);
    const [resultData, setResultData] = useState<Result[][]>([]);
    const [processedData, setProcessedData] = useState<ProcessedImageData[]>([]);
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);

    useEffect(() => {
        if (!images || !images.length) return;
        
        const localData = fetchFromLocalStorage(projectId);
        if(!localData){
            fetchResult();
        }else{
            setResultData(localData);
            setLoading(false);
        }
    
        return () => {
            console.log("cleaning up");
            setResultData([]);
        };
    }, [images]);

    useEffect(() => {
        if (resultData.length && images && images.length > 0) {
            const processed = resultData.map((results, index) => {
                const image = images[index];
                if (!image) {
                    console.warn(`No image found at index ${index} for result data`);
                    return null;
                }
                return processImageData(results, image, index);
            }).filter(Boolean) as ProcessedImageData[]; // Filter out null values
            
            setProcessedData(processed);
        }
    }, [resultData, images]);

    const fetchResult = async () => {
        const BATCH_SIZE = 10; // Set your safe batch size
        const data: any = [];

        // Break the images into chunks (batches) to process them sequentially
        const imageBatches = chunk(images, BATCH_SIZE);

        // Process each batch sequentially
        for (const batch of imageBatches) {
            const batchRequests = batch.map((image: any) => {
                return axiosWithAuth({})
                    .then(axios => axios.post('/result', { imageId: image.id }))
                    .then((res: any) => res.data)
                    .catch((err: any) => {
                        console.log(`Error fetching data for image ${image.id}:`, err);
                        return []; // Return empty array for failed requests to maintain array alignment
                    });
            });

            // Wait for the current batch to finish before starting the next one
            const batchResults = await Promise.all(batchRequests);

            // Add all results (including empty arrays for failed requests) to maintain alignment
            data.push(...batchResults);
        }

        saveToLocalStorage(projectId, data)
        setResultData(data);
        setLoading(false);
        return data
    };

    const refreshData = async () => {
        setLoading(true)
        clearLocalStorage(projectId);
        const data = await fetchResult();
        saveToLocalStorage(projectId, data);
        setLoading(false)
    }

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            // Cycle through sort directions: null -> asc -> desc -> null
            setSortDirection(current => {
                if (current === null) return 'asc';
                if (current === 'asc') return 'desc';
                return null;
            });
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return <FaSort className="ml-1 opacity-50" />;
        if (sortDirection === 'asc') return <FaSortUp className="ml-1" />;
        if (sortDirection === 'desc') return <FaSortDown className="ml-1" />;
        return <FaSort className="ml-1 opacity-50" />;
    };

    const sortedData = sortProcessedData(processedData, sortField, sortDirection);

    // Helpers for modern UI
    const formatPercent = (value?: number) => {
        if (value === undefined || value === null || Number.isNaN(Number(value))) return '—';
        return `${Number(value).toFixed(1)}%`;
    };

    const statusBadgeClass = (status: string) => {
        const s = (status || '').toLowerCase();
        if (s.includes('fail')) return 'badge-error';
        if (s.includes('processing') || s.includes('queued') || s.includes('pending')) return 'badge-warning';
        if (s.includes('success') || s.includes('complete')) return 'badge-success';
        return 'badge-ghost';
    };

    // Handle loading and error states
    if (isLoading || isProjectLoading || loading) {
        return <div><LoadingSpiner /></div>;
    }

    if (isError || isProjectError) {
        return <div>Error loading.</div>;
    }

    if (!processedData.length) {
        return <div>No results found.</div>;
    }

    // Render the images
    return (
        <div className="print:bg-transparent">
            <div className='print:hidden flex items-center gap-2'>
                <DownloadCSVButton data={resultData} images={images} />
                <ActionButton text="PDF" Icon={FaFilePdf} />
                <ActionButton text="Print" Icon={FaPrint} />
                <ActionButton onClick={refreshData} text="Refresh Data" Icon={FaArrowCircleRight}/>
            </div>
            <h3 className="text-xl font-semibold mt-4">{project.name} report</h3>

            <div className="mt-4 overflow-auto max-h-[70vh] rounded-xl border border-neutral-200 dark:border-neutral-700 shadow-sm print:border-0">
                <table className="table table-zebra table-pin-rows w-full min-w-[1100px] text-sm">
                    <thead className="bg-neutral-50/70 dark:bg-neutral-800/70 backdrop-blur sticky top-0 z-10">
                        <tr>
                            <th className='print:p-0 print:pr-2 text-neutral-500 font-semibold uppercase tracking-wide text-xs'>#</th>
                            <th 
                                className='print:p-0 print:pr-2 cursor-pointer select-none text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                onClick={() => handleSort('name')}
                            >
                                <div className="flex items-center">Image {getSortIcon('name')}</div>
                            </th>
                            <th 
                                className='print:p-0 print:pr-2 cursor-pointer select-none text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                onClick={() => handleSort('status')}
                            >
                                <div className="flex items-center">Status {getSortIcon('status')}</div>
                            </th>
                            <th 
                                className='print:p-0 print:pr-2 cursor-pointer select-none text-neutral-600 text-right hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                onClick={() => handleSort('qualityGood')}
                            >
                                <div className="flex items-center justify-end">Quality Good % {getSortIcon('qualityGood')}</div>
                            </th>
                            <th 
                                className='print:p-0 print:pr-2 cursor-pointer select-none text-neutral-600 text-right hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                onClick={() => handleSort('qualityPoor')}
                            >
                                <div className="flex items-center justify-end">Quality Poor % {getSortIcon('qualityPoor')}</div>
                            </th>
                            <th 
                                className='print:p-0 print:pr-2 cursor-pointer select-none text-neutral-600 text-right hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                onClick={() => handleSort('tumor')}
                            >
                                <div className="flex items-center justify-end">Tumor % {getSortIcon('tumor')}</div>
                            </th>
                            <th 
                                className='print:p-0 print:pr-2 cursor-pointer select-none text-neutral-600 text-right hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                onClick={() => handleSort('stroma')}
                            >
                                <div className="flex items-center justify-end">Stroma % {getSortIcon('stroma')}</div>
                            </th>
                            <th 
                                className='print:p-0 print:pr-2 cursor-pointer select-none text-neutral-600 text-right hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                onClick={() => handleSort('necrosis')}
                            >
                                <div className="flex items-center justify-end">Necrosis % {getSortIcon('necrosis')}</div>
                            </th>
                            <th 
                                className='print:p-0 print:pr-2 cursor-pointer select-none text-neutral-600 text-right hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                onClick={() => handleSort('lymphocyte')}
                            >
                                <div className="flex items-center justify-end">Lymphocyte % {getSortIcon('lymphocyte')}</div>
                            </th>
                            <th 
                                className='print:p-0 print:pr-2 cursor-pointer select-none text-neutral-600 text-right hover:bg-neutral-100 dark:hover:bg-neutral-700'
                                onClick={() => handleSort('backgroundMucin')}
                            >
                                <div className="flex items-center justify-end">Mucin % {getSortIcon('backgroundMucin')}</div>
                            </th>
                            <th className='print:p-0 print:pr-2 text-neutral-500 font-semibold uppercase tracking-wide text-xs'>Inspect</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedData.map((data) => {
                            const resultId = data.segmentationResult?.id || data.qualityResult?.id;
                            const imageLink = resultId ? `/projects/${projectId}/images/${data.segmentationResult?.image_id || data.qualityResult?.image_id}/results/${resultId}` : '#';

                            return (
                                <tr key={data.rowNumber} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/60">
                                    <td className='print:p-0 print:pr-2 text-neutral-500'>{data.rowNumber}</td>
                                    <td className='print:p-0 print:pr-2'>
                                        <span className="font-medium">{data.name}</span>
                                    </td>
                                    <td className='print:p-0 print:pr-2'>
                                        <span className={`badge ${statusBadgeClass(data.status)} badge-sm`}>{data.status}</span>
                                    </td>
                                    <td className='print:p-0 print:pr-2 text-right'>
                                        <div className="flex items-center justify-end gap-3">
                                            <span className="tabular-nums">{formatPercent(data.qualityGood)}</span>
                                            <progress className="progress progress-success w-24 hidden sm:block" value={Number(data.qualityGood) || 0} max={100}></progress>
                                        </div>
                                    </td>
                                    <td className='print:p-0 print:pr-2 text-right'>
                                        <div className="flex items-center justify-end gap-3">
                                            <span className="tabular-nums">{formatPercent(data.qualityPoor)}</span>
                                            <progress className="progress progress-error w-24 hidden sm:block" value={Number(data.qualityPoor) || 0} max={100}></progress>
                                        </div>
                                    </td>
                                    <td className='print:p-0 print:pr-2 text-right'>
                                        <div className="flex items-center justify-end gap-3">
                                            <span className="tabular-nums">{formatPercent(data.tumor)}</span>
                                            <progress className="progress progress-primary w-24 hidden sm:block" value={Number(data.tumor) || 0} max={100}></progress>
                                        </div>
                                    </td>
                                    <td className='print:p-0 print:pr-2 text-right'>
                                        <div className="flex items-center justify-end gap-3">
                                            <span className="tabular-nums">{formatPercent(data.stroma)}</span>
                                            <progress className="progress progress-primary w-24 hidden sm:block" value={Number(data.stroma) || 0} max={100}></progress>
                                        </div>
                                    </td>
                                    <td className='print:p-0 print:pr-2 text-right'>
                                        <div className="flex items-center justify-end gap-3">
                                            <span className="tabular-nums">{formatPercent(data.necrosis)}</span>
                                            <progress className="progress progress-primary w-24 hidden sm:block" value={Number(data.necrosis) || 0} max={100}></progress>
                                        </div>
                                    </td>
                                    <td className='print:p-0 print:pr-2 text-right'>
                                        <div className="flex items-center justify-end gap-3">
                                            <span className="tabular-nums">{formatPercent(data.lymphocyte)}</span>
                                            <progress className="progress progress-primary w-24 hidden sm:block" value={Number(data.lymphocyte) || 0} max={100}></progress>
                                        </div>
                                    </td>
                                    <td className='print:p-0 print:pr-2 text-right'>
                                        <div className="flex items-center justify-end gap-3">
                                            <span className="tabular-nums">{formatPercent(data.backgroundMucin)}</span>
                                            <progress className="progress progress-primary w-24 hidden sm:block" value={Number(data.backgroundMucin) || 0} max={100}></progress>
                                        </div>
                                    </td>
                                    <td className='print:p-0 print:pr-2'>
                                        {resultId ? (
                                            <Link to={imageLink} className="btn btn-ghost btn-sm">Inspect</Link>
                                        ) : (
                                            <span className="text-neutral-400">N/A</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}